// Tenantra Inc. - Login Page (Warm Sand Theme - Microsoft Style)
import { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo, LogoMark } from '@/components/Logo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { login } from '@/services/auth';
import MFALogin from '@/components/MFALogin';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function SignInTransition({ to }) {
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState('Credentials verified');
  useEffect(() => {
    const t1 = setTimeout(() => setProgress(60), 30);
    const t2 = setTimeout(() => { setLabel('Opening your dashboard'); setProgress(100); }, 420);
    const t3 = setTimeout(() => { window.location.href = to; }, 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [to]);
  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="signin-transition">
      <div className="w-56 text-center animate-in fade-in duration-300">
        <LogoMark className="w-10 h-10 mx-auto mb-6" />
        <div className="h-0.5 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-600 rounded-full transition-[width] duration-500 ease-out" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-3 text-xs text-slate-500" aria-live="polite">{label}</p>
      </div>
    </div>
  );
}

const destinationFor = (role) => {
  if (role === 'admin') return '/portal-8k2m9x/dashboard';
  if (role === 'landlord') return '/landlord/dashboard';
  return '/';
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState('');
  const recaptchaRef = useRef(null);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState('');
  const [phoneLastFour, setPhoneLastFour] = useState('');
  const [emailMasked, setEmailMasked] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Try to get reCAPTCHA token, but don't block login if it fails
      let recaptchaToken = null;
      if (recaptchaRef.current && RECAPTCHA_SITE_KEY) {
        try {
          recaptchaToken = await Promise.race([
            recaptchaRef.current.executeAsync(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('reCAPTCHA timeout')), 1500))
          ]);
          recaptchaRef.current.reset();
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without it:', recaptchaError);
          // Continue without reCAPTCHA token
        }
      }
      
      const response = await login(email, password, recaptchaToken);
      
      // Check if MFA is required
      if (response.mfa_required) {
        setMfaRequired(true);
        setMfaToken(response.mfa_token);
        setMfaMethod(response.mfa_method);
        setPhoneLastFour(response.phone_last_four || '');
        setEmailMasked(response.email_masked || '');
        setLoading(false);
        return;
      }
      
      setRedirectTo(destinationFor(response.role));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
      setLoading(false);
    }
  };

  const handleMFASuccess = (data) => {
    // Store token and user using correct keys
    localStorage.setItem('tenantra_access_token', data.token);
    localStorage.setItem('tenantra_user', JSON.stringify(data.user));
    
    setRedirectTo(destinationFor(data.user?.role));
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaToken('');
    setMfaMethod('');
    setPhoneLastFour('');
    setEmailMasked('');
  };

  if (redirectTo) return <SignInTransition to={redirectTo} />;

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col">
      <header className="h-16 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center">
          <Logo size="md" data-testid="auth-logo" />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-16">
        <div className="w-full max-w-[420px]">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 shadow-sm">
            {mfaRequired ? (
              /* MFA Verification Screen */
              <MFALogin
                mfaToken={mfaToken}
                mfaMethod={mfaMethod}
                phoneLastFour={phoneLastFour}
                emailMasked={emailMasked}
                onSuccess={handleMFASuccess}
                onCancel={handleMFACancel}
              />
            ) : (
              /* Regular Login Form */
              <>
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-stone-900">Sign in</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert className="bg-red-50 border-red-200 text-red-600 py-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-stone-700 text-sm">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-stone-700 text-sm">Password</Label>
                      <a href="/forgot-password" className="text-xs text-teal-600 hover:text-teal-700 transition-colors">
                        Forgot password?
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 pr-10 text-sm"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Invisible reCAPTCHA */}
                  {RECAPTCHA_SITE_KEY && (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      size="invisible"
                      badge="bottomright"
                    />
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0 transition-colors duration-200"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </form>

                <div className="mt-5 pt-5 border-t border-stone-200">
                  <p className="text-center text-sm text-stone-500">
                    Don't have an account?{' '}
                    <a href="/register" className="text-teal-600 hover:text-teal-700 font-medium transition-colors">
                      Register as Landlord
                    </a>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Tenant link */}
          {!mfaRequired && (
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                Looking for your Rent Report?{' '}
                <a href="/tenant/access" className="text-teal-700 hover:text-teal-800 font-medium transition-colors">
                  Go to Tenant Portal
                </a>
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Tenantra Inc. ·{' '}
        <a href="/privacy-policy" className="hover:text-slate-700">Privacy</a> ·{' '}
        <a href="/terms-of-service" className="hover:text-slate-700">Terms</a>
      </footer>
    </div>
  );
}
